import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import store from '@/store';
import DateRange from '@/components/charts/DateRange';
import LineChart from '@/components/charts/LineChart';
import analytics from '@/store/modules/analytics';
import { chart } from '@/_helpers';
import template from './LineChartContainer.vue';

type DarkModeType = 'dark' | 'light';

@Component({
	mixins: [template],
	components: {
		LineChart,
		DateRange
	}
})
export default class LineChartContainer extends Vue {
	@Prop() chartTitle!: string;

	@Prop() titles!: Array<string>;

	@Prop() queries!: Array<{}>;

	get darkMode(): boolean {
		return store.state.darkMode;
	}

	get colorMode(): DarkModeType {
		return store.state.darkMode ? 'dark' : 'light';
	}

	optionColors = {
		dark: {
			font: 'rgba(255, 255, 255, 0.8)',
			gridLines: 'rgba(255, 255, 255, 0.3)',
			ticks: 'rgba(255, 255, 255, 0.7)'
		},
		light: {
			font: 'rgba(0, 0, 0, 0.8)',
			gridLines: 'rgba(0, 0, 0, 0.3)',
			ticks: 'rgba(0,0,0, 0.7)'
		}
	};

	@Watch('darkMode')
	onDarkModeChange(): void {
		this.updateOptions();
	}

	@Watch('queries')
	onQueriesChange(): void {
		this.updateChart();
	}

	loading = true;

	error = null as any;

	chartData = {};

	chart = 'line';

	options: object | null = null;

	colors = [['#fd1056'], ['#64b9a5']];

	selectedDateRange = {} as any;

	mounted() {
		this.updateOptions();
		this.updateChart();
	}

	updateOptions() {
		this.options = {
			responsive: true,
			scaleShowGridLines: false,
			showScale: false,
			maintainAspectRatio: false,
			barShowStroke: false,
			borderColor: 'red',
			elements: {
				point: {
					radius: 6,
					backgroundColor: '#fd1056'
				}
			},
			legend: {
				labels: {
					padding: 16,
					fontSize: 16,
					fontColor: this.optionColors[this.colorMode].font
				}
			},
			tooltips: {
				titleFontSize: 16,
				bodyFontSize: 16,
				bodySpacing: 4,
				xPadding: 12,
				yPadding: 12
			},
			pointStyle: '',
			scales: {
				xAxes: [
					{
						gridLines: {
							borderDash: [8, 4],
							color: this.optionColors[this.colorMode].gridLines
						},
						type: 'time',
						distribution: 'linear',
						time: {
							displayFormats: {
								minute: 'HH:mm',
								hour: 'HH:mm',
								day: 'MMM DD',
								week: 'MMM DD',
								month: 'MMM YYYY',
								quarter: 'MMM YYYY',
								year: 'MMM YYYY'
							},
							minUnit: 'day'
						},
						ticks: {
							fontColor: this.optionColors[this.colorMode].ticks,
							fontSize: 16
						}
					}
				],
				yAxes: [
					{
						gridLines: {
							// borderDash: [8, 4],
							color: this.optionColors[this.colorMode].gridLines
						},
						ticks: {
							suggestedMin: 0,
							suggestedMax: 30,
							fontColor: this.optionColors[this.colorMode].ticks,
							fontSize: 16
						}
					}
				]
			}
		};
	}

	async updateChart() {
		this.error = null;
		this.loading = true;

		const format = 'YYYY-MM-DD';

		if (!this.selectedDateRange || !this.selectedDateRange.date) {
			return;
			// TODO: ERROR
		}

		const startDate = this.selectedDateRange.date.start.format(format);
		const endDate = this.selectedDateRange.date.end.format(format);

		const dateRange = chart.getDateRange(startDate, endDate);

		const requests = [];

		for (let i = 0; i < this.queries.length; i++) {
			(this.queries[i] as any).period = dateRange;

			if (dateRange === 'custom') {
				(this.queries[i] as any).date = `${startDate},${endDate}`;
			} else {
				(this.queries[i] as any).date = `${startDate}`;
			}

			(this.queries[i] as any).type = this.chart;

			requests.push(analytics.getChartData(this.queries[i]));
		}

		let responses;

		try {
			responses = await Promise.all(requests);
		} catch (err) {
			this.error = err;
			this.loading = false;
			return;
		}

		this.chartData = {
			labels: this.formatChartLabels(responses[0]),
			datasets: this.getDatasets(responses, this.queries)
		};

		this.loading = false;
	}

	getDatasets(responses: any, queries: any) {
		const datasets = [];
		for (let i = 0; i < responses.length; i++) {
			const data = [] as any;

			let total = 0;

			responses[i].results.forEach((t: any) => {
				data.push(t[queries[i].metrics]);

				total += t[queries[i].metrics];
			});

			datasets.push({
				borderColor: this.colors[i],
				label: this.titles[i],
				data,
				total
			});
		}
		return datasets;
	}

	formatChartLabels(resp: any) {
		const labels: any = [];

		resp.results.forEach((t: any) => {
			labels.push(t.date);
		});

		return labels;
	}
}
